import { ReactNode, useLayoutEffect, useRef } from 'react';
import styles from './HeightMorph.module.css';

// Cross-fades between a collapsed and an expanded view while animating the container's height smoothly
// FROM the one's height TO the other's — a measured accordion (orion-web has no animation library, and
// the grid-rows 0fr→1fr trick can only grow from zero, which is the jump this replaces).
//
// Both views stay mounted; the inactive one lifts out of flow (top-anchored, no bottom inset, so its
// natural height is still measurable) and fades. `overflow` is clipped only DURING the morph, so at rest
// the collapsed view's card shadows aren't cut.
//
// The inactive layer is `inert`, not `aria-hidden`. It is faded and pointer-blocked but still at its
// full natural height and still in the tab order, so `aria-hidden` alone left every card and button in
// the hidden view keyboard-reachable while claiming they were not there — and a Tab into one puts focus
// inside an aria-hidden subtree, which Chrome blocks outright. See CLAUDE.md → "Hidden vs inert".
interface Props {
  expanded: boolean;
  collapsed: ReactNode;
  expandedContent: ReactNode;
}

export function HeightMorph({ expanded, collapsed, expandedContent }: Props) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const collapsedRef = useRef<HTMLDivElement>(null);
  const expandedRef = useRef<HTMLDivElement>(null);
  const first = useRef(true);

  useLayoutEffect(() => {
    // No entrance animation on first paint — only on a real toggle.
    if (first.current) {
      first.current = false;
      return;
    }
    const wrap = wrapRef.current;
    const leaving = expanded ? collapsedRef.current : expandedRef.current;
    const arriving = expanded ? expandedRef.current : collapsedRef.current;
    if (!wrap || !leaving || !arriving) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const from = leaving.offsetHeight;
    const to = arriving.offsetHeight;
    if (from === to) return;

    wrap.style.overflow = 'hidden';
    wrap.style.height = `${from}px`;
    void wrap.offsetHeight; // commit the start height before flipping to the target
    requestAnimationFrame(() => {
      if (wrapRef.current) wrapRef.current.style.height = `${to}px`;
    });

    const onEnd = (e: TransitionEvent) => {
      if (e.target !== wrap || e.propertyName !== 'height') return;
      wrap.style.height = '';
      wrap.style.overflow = '';
      wrap.removeEventListener('transitionend', onEnd);
    };
    wrap.addEventListener('transitionend', onEnd);
    return () => wrap.removeEventListener('transitionend', onEnd);
  }, [expanded]);

  return (
    <div ref={wrapRef} className={styles.wrap}>
      <div
        ref={collapsedRef}
        className={styles.layer}
        data-active={!expanded || undefined}
        inert={expanded}
      >
        {collapsed}
      </div>
      <div
        ref={expandedRef}
        className={styles.layer}
        data-active={expanded || undefined}
        inert={!expanded}
      >
        {expandedContent}
      </div>
    </div>
  );
}
